'use client'
import { useRouter, useSearchParams } from 'next/navigation'
import React, { useState, useCallback, useEffect } from 'react'

import { IBlogCategoryReduced } from '@/_types'

interface IProps {
	categories: IBlogCategoryReduced[]
	activeCategory?: string
	activeSearch?: string
	searchPlaceholder: string
}

const ABlogCategoriesInteractive = ({
	categories,
	activeCategory,
	activeSearch,
	searchPlaceholder
}: IProps) => {
	const searchParams = useSearchParams()
	const router = useRouter()

	const [showMore, setShowMore] = useState(false)
	const [searchValue, setSearchValue] = useState('')
	const [isHydrated, setIsHydrated] = useState(false)
	const visibleCount = 5

	const shownCategories = showMore
		? categories
		: (() => {
				const slicedCategories = categories.slice(0, visibleCount)
				const isPodcastCategory = slicedCategories.some(
					category => category.slug === 'podcasty'
				)

				if (isPodcastCategory) {
					return slicedCategories
				} else {
					const podcastCategory = categories.find(
						category => category.slug === 'podcasty'
					)
					if (podcastCategory) {
						return [...slicedCategories, podcastCategory]
					}

					return slicedCategories
				}
		  })()

	// Get the current category from URL params for client-side rendering
	const currentCategory = searchParams.get('category')

	// Initialize search value from URL params and mark as hydrated
	useEffect(() => {
		setSearchValue(activeSearch || '')
		setIsHydrated(true)
	}, [activeSearch])

	// Debounce utility function
	function debounce<T extends (...args: any[]) => any>(
		func: T,
		wait: number
	): (...args: Parameters<T>) => void {
		let timeout: NodeJS.Timeout
		return (...args: Parameters<T>) => {
			clearTimeout(timeout)
			timeout = setTimeout(() => func(...args), wait)
		}
	}

	// Debounced search function
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const debouncedSearch = useCallback(
		debounce((searchTerm: string) => {
			const params = new URLSearchParams(Array.from(searchParams.entries()))

			// Clear pagination when searching
			params.delete('page')

			if (searchTerm.trim()) {
				params.set('search', searchTerm.trim())
			} else {
				params.delete('search')
			}

			router.replace(`?${params.toString()}`)
		}, 300),
		[searchParams, router]
	)

	// Handle search input change
	const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value
		setSearchValue(value)
		debouncedSearch(value)
	}

	// Handle form submission (prevent default)
	const handleSearchSubmit = (e: React.FormEvent) => {
		e.preventDefault()
		// The search is already handled by the debounced function
	}

	const handleCategoryClick = (categorySlug: string) => {
		const params = new URLSearchParams(Array.from(searchParams.entries()))

		// Clear pagination and search when selecting category
		params.delete('page')
		params.delete('search')

		if (categorySlug === currentCategory) {
			params.delete('category')
		} else {
			params.set('category', categorySlug)
		}

		router.replace(`?${params.toString()}`)
	}

	// Use client-side category for active state after hydration
	const activeCategoryToUse = isHydrated ? currentCategory : activeCategory

	return (
		<>
			<div className='pos_rel item_search-on-page' style={{ position: 'relative' }}>
				<form name='blog-search-form' autoComplete='off' onSubmit={handleSearchSubmit}>
					<div className='pos_rel'>
						<input
							type='text'
							value={isHydrated ? searchValue : activeSearch || ''}
							onChange={isHydrated ? handleSearchChange : undefined}
							id='search-input'
							className='input'
							placeholder={searchPlaceholder}
							autoComplete='off'
							name='search'
							aria-label='Vyhľadávanie'
							readOnly={!isHydrated}
						/>
						<button
							type='submit'
							className='submit'
							name='search'
							aria-label='Vyhľadávanie'
						>
							<svg className='sicofill'>
								<use href='/assets/layout/icons.svg#s_mag' />
							</svg>
						</button>
					</div>
				</form>
			</div>
			<div className='lists-cat mgt-25'>
				<ul>
					{shownCategories &&
						shownCategories.map(category => (
							<li
								key={category.id}
								onClick={
									isHydrated
										? () => handleCategoryClick(category.slug)
										: undefined
								}
								className={activeCategoryToUse === category.slug ? 'active' : ''}
								style={{ cursor: isHydrated ? 'pointer' : 'default' }}
							>
								<span>{category.name}</span>
							</li>
						))}
					<li>
						<div
							className={`show-more-cat${showMore ? ' open' : ''}`}
							onClick={isHydrated ? () => setShowMore(v => !v) : undefined}
							style={{ cursor: isHydrated ? 'pointer' : 'default' }}
						></div>
					</li>
				</ul>
			</div>
		</>
	)
}

export default ABlogCategoriesInteractive
